import {
  DeliveredQuantity_Read,
  NullableReconcileState,
  Principal_Read_Nested,
  RutterExportState,
  RutterSettlementExport_Read_Nested,
  Settlement_Create,
  Settlement_Create_Line,
  Settlement_Data,
  Settlement_Read,
  Settlement_Read_Nested,
  Settlement_Update,
  SettlementEmailDraft_Read,
  SettlementEvent_Read,
  SettlementEventName,
  SettlementExportSide,
  SettlementLineItemGrouping,
  SettlementLineItemType,
  SettlementState,
  SettlementStateEvent,
  SettlementTicketAttachmentMode,
  UserBulkCreateSettlementChannel_Read,
  UserBulkExportSettlementsChannel_Read,
} from '@treadinc/horizon-api-spec';
import dayjs, { Dayjs } from 'dayjs';
import { isNil } from 'lodash-es';

import { createdBy as principalName } from '~components/Job/AuditDetails/utils';
import { SettlementDTO } from '~formsShared/components/SettlementFormFields';
import { AccountBasic } from '~hooks/useAccount/models';
import { DriverBasic } from '~hooks/useDrivers/models';
import {
  OutboundEmail,
  OutboundEmailDraft,
  OutboundEmailDraftAttachment,
  OutboundEmailDraftFields,
} from '~hooks/useOutboundEmails/models';
import { SettlementExportBasic } from '~hooks/useSettlementExports/models';
import { Nullable } from '~types/Nullable';

export type SettlementLine = Settlement_Create_Line;

export function extractGroupingsFromSettlementDTO(dto: SettlementDTO) {
  let groupings: SettlementLineItemGrouping[] = [];

  if (dto.settlementLineItemGroupings) {
    groupings = Object.entries(dto.settlementLineItemGroupings).reduce(
      (acc, [key, value]) => {
        if (value) {
          acc.push(key as SettlementLineItemGrouping);
        }

        return acc;
      },
      [] as SettlementLineItemGrouping[],
    );
  }

  return groupings.length ? groupings : undefined;
}

export function deparseSettlementCreateTerms(
  proto: Pick<SettlementDTO, 'dueDate' | 'notes' | 'paymentTermId'>,
) {
  return {
    due_date: proto.dueDate ? dayjs.tz(proto.dueDate).format('YYYY-MM-DD') : undefined,
    notes: proto.notes?.trim() ? proto.notes.trim() : undefined,
    // an absent term lets the API apply the company default; `null` means no term at all
    ...(proto.paymentTermId !== undefined
      ? { payment_term_id: proto.paymentTermId }
      : {}),
  };
}

export class Settlement {
  constructor(
    private _id: Nullable<string>,
    private _adjustments_total_amount: number,
    private _data: SettlementData,
    private _exported_at: Nullable<Dayjs>,
    private _jobs_count: number,
    private _state: SettlementState,
    private _total_amount: number,
    private _customer_account: Nullable<AccountBasic>,
    private _vendor_account: Nullable<AccountBasic>,
    private _driver: Nullable<DriverBasic>,
    private _settlement_id: string,
    private _ar_settlement_id: string,
    private _ap_settlement_id: string,
    private _created_at: Dayjs,
    private _updated_at: Dayjs,
    private _settlement_line_item_type: SettlementLineItemType,
    private _settlement_line_item_groupings: SettlementLineItemGrouping[],
    private _add_on_charges_total_amount: number,
    private _invoices_total_amount: number,
    private _state_events: SettlementStateEvent[],
    private _last_outbound_email: Nullable<OutboundEmail>,
    private _rebillable: boolean,
    private _original_settlement: Nullable<SettlementBasic>,
    private _rebill_total_amount: Nullable<number>,
    private _rebill_add_on_charges_total_amount: Nullable<number>,
    private _rebill_invoices_total_amount: Nullable<number>,
    private _void_reason: Nullable<string>,
    private _voided_at: Nullable<Dayjs>,
    private _delivered_quantities: DeliveredQuantity_Read[],
    private _editable: boolean,
    private _include_loads_in_pdf: boolean,
    private _rutter_settlement_export: Nullable<RutterSettlementExport>,
    private _invoices_tax_total_amount: Nullable<number>,
    private _rebill_invoices_tax_total_amount: Nullable<number>,
    private _reconcile_state: NullableReconcileState,
    private _due_date: Nullable<Dayjs>,
    private _notes: Nullable<string>,
    private _payment_term_id: Nullable<string> = null,
    private _payment_term_name: Nullable<string> = null,
    private _payment_term_due_days: Nullable<number> = null,
    private _last_settlement_export: Nullable<SettlementExportBasic> = null,
    private _export_eligible_sides: SettlementExportSide[] = [],
    private _rutter_line_items_needing_item_count: number = 0,
    private _pay_commission_total_amount: number = 0,
    private _rebill_pay_commission_total_amount: Nullable<number> = null,
  ) {}

  public get id() {
    return this._id;
  }

  public get adjustmentsTotalAmount() {
    return this._adjustments_total_amount;
  }

  public get data() {
    return this._data;
  }

  public get exportedAt() {
    return this._exported_at;
  }

  public get jobsCount() {
    return this._jobs_count;
  }

  public get state() {
    return this._state;
  }

  public get totalAmount() {
    return this._total_amount;
  }

  public get customerAccount() {
    return this._customer_account;
  }

  public get vendorAccount() {
    return this._vendor_account;
  }

  public get driver() {
    return this._driver;
  }

  public get settlementId() {
    return this._settlement_id;
  }

  public get arSettlementId() {
    return this._ar_settlement_id;
  }

  public get apSettlementId() {
    return this._ap_settlement_id;
  }

  public get createdAt() {
    return this._created_at;
  }

  public get updatedAt() {
    return this._updated_at;
  }

  public get settlementLineItemType() {
    return this._settlement_line_item_type;
  }

  public get settlementLineItemGroupings() {
    return this._settlement_line_item_groupings;
  }

  public get addOnChargesTotalAmount() {
    return this._add_on_charges_total_amount;
  }

  public get invoicesTotalAmount() {
    return this._invoices_total_amount;
  }

  public get stateEvents() {
    return this._state_events;
  }

  public get lastOutboundEmail() {
    return this._last_outbound_email;
  }

  public get rebillable() {
    return this._rebillable;
  }

  public get originalSettlement() {
    return this._original_settlement;
  }

  public get rebillTotalAmount() {
    return this._rebill_total_amount;
  }

  public get rebillAddOnChargesTotalAmount() {
    return this._rebill_add_on_charges_total_amount;
  }

  public get rebillInvoicesTotalAmount() {
    return this._rebill_invoices_total_amount;
  }

  public get voidReason() {
    return this._void_reason;
  }

  public get voidedAt() {
    return this._voided_at;
  }

  public get deliveredQuantities() {
    return this._delivered_quantities;
  }

  public get editable() {
    return this._editable;
  }

  public get includeLoadsInPdf() {
    return this._include_loads_in_pdf;
  }

  public get rutterSettlementExport() {
    return this._rutter_settlement_export;
  }

  public get invoicesTaxTotalAmount() {
    return this._invoices_tax_total_amount;
  }

  public get rebillInvoicesTaxTotalAmount() {
    return this._rebill_invoices_tax_total_amount;
  }

  public get reconcileState() {
    return this._reconcile_state;
  }

  public get dueDate() {
    return this._due_date;
  }

  public get paymentTermId() {
    return this._payment_term_id;
  }

  public get paymentTermName() {
    return this._payment_term_name;
  }

  public get paymentTermDueDays() {
    return this._payment_term_due_days;
  }

  public get notes() {
    return this._notes;
  }

  public get lastSettlementExport() {
    return this._last_settlement_export;
  }

  public get payCommissionTotalAmount() {
    return this._pay_commission_total_amount;
  }

  public get rebillPayCommissionTotalAmount() {
    return this._rebill_pay_commission_total_amount;
  }

  public get exportEligibleSides() {
    return this._export_eligible_sides;
  }

  public get rutterLineItemsNeedingItemCount() {
    return this._rutter_line_items_needing_item_count;
  }

  public static parse(proto: Settlement_Read) {
    return new Settlement(
      proto.id,
      Number(proto.adjustments_total_amount),
      SettlementData.parse(proto.data),
      proto.exported_at ? dayjs(proto.exported_at) : null,
      proto.jobs_count,
      proto.state,
      Number(proto.total_amount),
      proto.customer_account ? AccountBasic.parse(proto.customer_account) : null,
      proto.vendor_account ? AccountBasic.parse(proto.vendor_account) : null,
      proto.driver ? DriverBasic.parse(proto.driver) : null,
      proto.settlement_id,
      proto.ar_settlement_id,
      proto.ap_settlement_id,
      dayjs(proto.created_at),
      dayjs(proto.updated_at),
      proto.settlement_line_item_type,
      proto.settlement_line_item_groupings,
      Number(proto.add_on_charges_total_amount),
      Number(proto.invoices_total_amount),
      proto.state_events,
      proto.last_outbound_email ? OutboundEmail.parse(proto.last_outbound_email) : null,
      proto.rebillable,
      proto.original_settlement ? SettlementBasic.parse(proto.original_settlement) : null,
      isNil(proto.rebill_total_amount) ? null : Number(proto.rebill_total_amount),
      isNil(proto.rebill_add_on_charges_total_amount)
        ? null
        : Number(proto.rebill_add_on_charges_total_amount),
      isNil(proto.rebill_invoices_total_amount)
        ? null
        : Number(proto.rebill_invoices_total_amount),
      proto.void_reason,
      proto.voided_at ? dayjs(proto.voided_at) : null,
      proto.delivered_quantities || [],
      proto.editable,
      proto.include_loads_in_pdf,
      proto.rutter_settlement_export
        ? RutterSettlementExport.parse(proto.rutter_settlement_export)
        : null,
      isNil(proto.invoices_tax_total_amount)
        ? null
        : Number(proto.invoices_tax_total_amount),
      isNil(proto.rebill_invoices_tax_total_amount)
        ? null
        : Number(proto.rebill_invoices_tax_total_amount),
      proto.reconcile_state,
      // dayjs.tz keeps the calendar date stable across machine timezones for the
      // date-only string the API returns
      proto.due_date ? dayjs.tz(proto.due_date) : null,
      proto.notes ?? null,
      proto.payment_term?.id ?? null,
      proto.payment_term_name ?? null,
      proto.payment_term_due_days ?? null,
      proto.last_settlement_export
        ? SettlementExportBasic.parse(proto.last_settlement_export)
        : null,
      proto.export_eligible_sides,
      proto.rutter_line_items_needing_item_count,
      Number(proto.pay_commission_total_amount),
      isNil(proto.rebill_pay_commission_total_amount)
        ? null
        : Number(proto.rebill_pay_commission_total_amount),
    );
  }

  // The API takes `invoice_ids` or `lines`, never both
  public static deparse(proto: SettlementDTO) {
    const lines = proto.lines?.length ? proto.lines : undefined;
    const invoiceIds = proto.invoiceIds?.length ? proto.invoiceIds : undefined;
    const hasNothingToSettle = !lines && !invoiceIds;

    if (hasNothingToSettle) {
      throw new Error('Invoice ids or lines not set');
    }

    const isLoadType = proto.settlementLineItemType === SettlementLineItemType.LOAD;

    const data: Settlement_Create = {
      ...(lines ? { lines } : { invoice_ids: invoiceIds }),
      settlement_line_item_type: proto.settlementLineItemType,
      settlement_line_item_groupings: extractGroupingsFromSettlementDTO(proto),
      include_loads_in_pdf: isLoadType ? false : proto.allLoadsAndTicketNumbers,
      ...deparseSettlementCreateTerms(proto),
    };

    return data;
  }

  public static deparseUpdate(proto: SettlementDTO) {
    const isLoadType = proto.settlementLineItemType === SettlementLineItemType.LOAD;

    const data: Settlement_Update = {
      settlement_line_item_type: proto.settlementLineItemType,
      settlement_line_item_groupings: extractGroupingsFromSettlementDTO(proto) ?? [],
      include_loads_in_pdf: isLoadType ? false : proto.allLoadsAndTicketNumbers,
    };

    // A locked (approved) settlement rejects due_date/notes at the API, so they only
    // travel in the payload while the settlement is still editable. An absent key means
    // "leave untouched" so a caller editing one field cannot write a stale value over the
    // other; `null` stays the explicit clear. The payment term is not gated the same way:
    // every state a settlement can be exported from is locked, so a term that stopped
    // travelling on approval could never reach an export.
    if (proto.editable) {
      if (proto.dueDate !== undefined) {
        data.due_date = proto.dueDate
          ? dayjs.tz(proto.dueDate).format('YYYY-MM-DD')
          : null;
      }

      if (proto.notes !== undefined) {
        data.notes = proto.notes?.trim() ? proto.notes.trim() : null;
      }
    }

    // The Tread payment term travels only when the caller supplies it; the server stamps
    // the name/due-days snapshots and derives the due date itself.
    if (proto.paymentTermId !== undefined) {
      data.payment_term_id = proto.paymentTermId ?? null;
    }

    // The term lives on the Rutter export row and travels only when the caller supplies
    // it: every other settlement save resends a defaults blob, and an absent term there
    // must not clear the one already chosen.
    if (proto.externalPaymentTermsId !== undefined) {
      data.external_payment_terms_id = proto.externalPaymentTermsId ?? null;
      data.payment_terms_name = proto.paymentTermsName ?? null;
      // due on receipt is a real term whose due days are zero
      data.payment_terms_due_days = proto.paymentTermsDueDays ?? null;
    }

    return data;
  }
}

export class SettlementData {
  constructor(
    private _friendly_order_ids: Nullable<string[]>,
    private _quantities_per_uom: Nullable<string[]>,
  ) {}

  public get friendlyOrderIds() {
    return this._friendly_order_ids;
  }

  public get quantitiesPerUom() {
    return this._quantities_per_uom;
  }

  public static parse(proto: Settlement_Data) {
    return new SettlementData(proto.friendly_order_ids, proto.quantities_per_uom);
  }
}

export class SettlementBasic {
  constructor(
    private _id: string,
    private _settlement_id: string,
    private _ar_settlement_id: string,
    private _ap_settlement_id: string,
  ) {}

  public get id() {
    return this._id;
  }

  public get settlementId() {
    return this._settlement_id;
  }

  public get arSettlementId() {
    return this._ar_settlement_id;
  }

  public get apSettlementId() {
    return this._ap_settlement_id;
  }

  public static parse(proto: Settlement_Read_Nested) {
    return new SettlementBasic(
      proto.id,
      proto.settlement_id,
      proto.ar_settlement_id ?? '',
      proto.ap_settlement_id ?? '',
    );
  }
}

export class SettlementEvent {
  constructor(
    private _id: string,
    private _event_name: SettlementEventName,
    private _occurred_at: Dayjs,
    private _created_by: Nullable<Principal_Read_Nested>,
  ) {}

  public get id() {
    return this._id;
  }

  public get eventName() {
    return this._event_name;
  }

  public get occurredAt() {
    return this._occurred_at;
  }

  public get createdByName() {
    return this._created_by ? principalName(this._created_by) : '';
  }

  public static parse(proto: SettlementEvent_Read) {
    return new SettlementEvent(
      proto.id,
      proto.event_name,
      dayjs(proto.occurred_at),
      proto.created_by ?? null,
    );
  }
}

export class BulkSettlementCreateResult {
  constructor(
    private _created_customer_settlements: BulkSettlementCreateResultItem,
    private _created_vendor_settlements: BulkSettlementCreateResultItem,
    private _created_driver_settlements: BulkSettlementCreateResultItem,
    private _skipped_lines: BulkSettlementSkippedLines[],
  ) {}

  public get createdCustomerSettlements() {
    return this._created_customer_settlements;
  }

  public get createdVendorSettlements() {
    return this._created_vendor_settlements;
  }

  public get createdDriverSettlements() {
    return this._created_driver_settlements;
  }

  public get skippedLines() {
    return this._skipped_lines;
  }

  public static parse(proto: UserBulkCreateSettlementChannel_Read) {
    return new BulkSettlementCreateResult(
      BulkSettlementCreateResultItem.parse(proto.created_customer_settlements),
      BulkSettlementCreateResultItem.parse(proto.created_vendor_settlements),
      BulkSettlementCreateResultItem.parse(proto.created_driver_settlements),
      Object.entries(proto.skipped_lines ?? {})
        .filter(([, count]) => count > 0)
        .map(BulkSettlementSkippedLines.parse),
    );
  }
}

class BulkSettlementSkippedLines {
  constructor(
    private _reason: string,
    private _count: number,
  ) {}

  public get count() {
    return this._count;
  }

  public get reason() {
    return this._reason;
  }

  public static parse([reason, count]: [string, number]) {
    return new BulkSettlementSkippedLines(reason, count);
  }
}

class BulkSettlementCreateResultItem {
  constructor(
    private _count: number,
    private _total_amount: number,
  ) {}

  public get count() {
    return this._count;
  }

  public get totalAmount() {
    return this._total_amount;
  }

  public static parse(
    proto:
      | UserBulkCreateSettlementChannel_Read['created_customer_settlements']
      | UserBulkCreateSettlementChannel_Read['created_vendor_settlements']
      | UserBulkCreateSettlementChannel_Read['created_driver_settlements'],
  ) {
    return new BulkSettlementCreateResultItem(proto.count, Number(proto.total_amount));
  }
}

class BulkSettlementExportFailure {
  constructor(
    private _settlement_id: string,
    private _message: string,
  ) {}

  public get settlementId() {
    return this._settlement_id;
  }

  public get message() {
    return this._message;
  }

  public static parse(proto: UserBulkExportSettlementsChannel_Read['failed'][number]) {
    return new BulkSettlementExportFailure(proto.settlement_id, proto.message);
  }
}

export class BulkSettlementExportResult {
  constructor(
    private _queued_settlement_ids: string[],
    private _pending_settlement_ids: string[],
    private _already_exported_settlement_ids: string[],
    private _failed: BulkSettlementExportFailure[],
  ) {}

  public get queuedSettlementIds() {
    return this._queued_settlement_ids;
  }

  public get skippedCount() {
    return (
      this._pending_settlement_ids.length + this._already_exported_settlement_ids.length
    );
  }

  public get failures() {
    return this._failed;
  }

  public get settlementIds() {
    return [
      ...this._queued_settlement_ids,
      ...this._pending_settlement_ids,
      ...this._already_exported_settlement_ids,
      ...this._failed.map((failure) => failure.settlementId),
    ];
  }

  public static parse(proto: UserBulkExportSettlementsChannel_Read) {
    return new BulkSettlementExportResult(
      proto.queued_settlement_ids,
      proto.pending_settlement_ids,
      proto.already_exported_settlement_ids,
      proto.failed.map(BulkSettlementExportFailure.parse),
    );
  }
}

export class SettlementFor {
  constructor(
    private _name: string,
    private _record: Nullable<AccountBasic> | Nullable<DriverBasic>,
  ) {}

  public get name() {
    return this._name;
  }

  public get record() {
    return this._record;
  }
}

export class RutterSettlementExport {
  constructor(
    private _id: string,
    private _state: RutterExportState,
    private _exported_at: Nullable<Dayjs>,
    private _last_error: Nullable<string>,
    private _external_payment_terms_id: Nullable<string>,
    private _payment_terms_name: Nullable<string>,
    private _payment_terms_due_days: Nullable<number>,
  ) {}

  public get id() {
    return this._id;
  }

  public get state() {
    return this._state;
  }

  public get exportedAt() {
    return this._exported_at;
  }

  public get lastError() {
    return this._last_error;
  }

  public get externalPaymentTermsId() {
    return this._external_payment_terms_id;
  }

  public get paymentTermsName() {
    return this._payment_terms_name;
  }

  public get paymentTermsDueDays() {
    return this._payment_terms_due_days;
  }

  public static parse(proto: RutterSettlementExport_Read_Nested) {
    return new RutterSettlementExport(
      proto.id,
      proto.state,
      proto.exported_at ? dayjs(proto.exported_at) : null,
      proto.last_error ?? null,
      proto.external_payment_terms_id ?? null,
      proto.payment_terms_name ?? null,
      proto.payment_terms_due_days ?? null,
    );
  }
}

export interface SettlementTicketAttachmentOptions {
  attachTickets: boolean;
  ticketAttachmentMode: SettlementTicketAttachmentMode;
}

export const DEFAULT_SETTLEMENT_TICKET_ATTACHMENT_OPTIONS: SettlementTicketAttachmentOptions =
  {
    attachTickets: true,
    ticketAttachmentMode: SettlementTicketAttachmentMode.MERGED,
  };

export class SettlementEmailDraft extends OutboundEmailDraft {
  constructor(
    private _settlement_ids: string[],
    draft: OutboundEmailDraftFields,
  ) {
    super(draft);
  }

  public static parse(proto: SettlementEmailDraft_Read, attachTickets: boolean) {
    return new SettlementEmailDraft(proto.settlement_ids, {
      ...OutboundEmailDraft.parseFields(proto),
      attachments: proto.attachments.map((attachment) =>
        OutboundEmailDraftAttachment.parse(
          attachment,
          attachTickets ? attachment.ticket_count : null,
        ),
      ),
    });
  }

  public get ids() {
    return this._settlement_ids;
  }
}
